/**
 * What a model says, without how it is written (part 04 log, table RB): the text `replaceText` compares when it
 * checks that a chapter it wrote reads back as the model it meant to write.
 *
 * Kept: block kinds and nesting, heading levels, list starts and looseness, note labels; text, and which of its
 * characters are the unmatched part of a delimiter run (they decide how the rest pairs); soft and hard breaks;
 * spans with their delimiters; link and image targets; note references; bracket pairs kept for what they hold.
 * Left out, as writing: positions, whether a character is written literally, escaped or as a reference, a hard
 * break's style (N5), where one text run ends and the next starts, a bracket pair holding no delimiter, which
 * is written and read as its text (I6), and the looseness of a list with nothing to separate (one item holding at
 * most one block, which an edit can leave: no source writes it loose).
 */
import { holdsDelimiter } from './inline.ts';
import type { Inline } from './inline.ts';
import type { Block } from './model.ts';

/** A value of the shape: nested arrays of strings, numbers, flags and nulls. */
type Shape = string | number | boolean | null | readonly Shape[];

/** Builds a content list's shape, joining text across run edges; a change of class starts a new piece. */
class InlineShape {
  readonly out: Shape[] = [];
  private text = '';
  private delimiter = false;

  char(text: string, delimiter: boolean): void {
    // flush writes nothing while no text is pending.
    if (delimiter !== this.delimiter) this.flush();
    this.text += text;
    this.delimiter = delimiter;
  }

  item(shape: Shape): void {
    this.flush();
    this.out.push(shape);
  }

  flush(): void {
    if (this.text !== '') this.out.push([this.delimiter ? 'delimiter' : 'text', this.text]);
    this.text = '';
  }
}

function addInline(content: readonly Inline[], to: InlineShape): void {
  for (const item of content) {
    switch (item.kind) {
      case 'text':
        for (const piece of item.pieces) to.char(piece.text, piece.origin === 'delimiter');
        break;
      case 'soft-break':
        to.item('soft');
        break;
      case 'hard-break':
        to.item('hard');
        break;
      case 'emphasis':
      case 'strong':
      case 'small-caps':
        to.item([item.kind, item.open, item.close, inlineShape(item.children)]);
        break;
      case 'link':
      case 'image':
        to.item([item.kind, item.destination, item.title, inlineShape(item.children)]);
        break;
      case 'footnote-reference':
        to.item(['note', item.label]);
        break;
      case 'brackets':
        if (holdsDelimiter(item.children)) to.item(['brackets', item.open, item.close, inlineShape(item.children)]);
        else addInline(item.text, to);
    }
  }
}

function inlineShape(content: readonly Inline[]): Shape[] {
  const to = new InlineShape();
  addInline(content, to);
  to.flush();
  return to.out;
}

function blockShape(block: Block): Shape {
  switch (block.kind) {
    case 'paragraph':
    case 'centered':
      return [block.kind, inlineShape(block.content)];
    case 'heading':
      return ['heading', block.level, inlineShape(block.content)];
    case 'scene-break':
      return ['scene-break'];
    case 'quote':
      return ['quote', block.blocks.map(blockShape)];
    case 'list':
      // One item holding at most one block has nothing a blank line could separate, so no source writes it loose.
      return ['list', block.start, block.loose && (block.items.length > 1 || block.items.some((item) => item.length > 1)), block.items.map((item) => item.map(blockShape))];
    case 'footnote':
      return ['footnote', block.label, block.blocks.map(blockShape)];
  }
}

/** The shape of `blocks` as a string: two block lists say the same thing exactly when their shapes are equal. */
export function modelShape(blocks: readonly Block[]): string {
  return JSON.stringify(blocks.map(blockShape));
}
